import { useState, useContext, useEffect } from "react";
import {
  OutlinedInput,
  InputLabel,
  MenuItem,
  FormControl,
  Select,
  TextField,
  Card,
  CardHeader,
  Box,
  useMediaQuery,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";
import { useSnackbar } from "../Utility/UserMessageContext";
import authContext from "../Context/AuthProvider";
import { showLoadingImage } from "../Utility/Utility";
import MenuBar from "../common/MenuBar";
import { Genders } from "../Utility/Constants";
import { getEnquiriesById, updateEnquiry } from "../API/EnquiryApi";
import EnquiryComments from "./EnquiryComments";
import { LocalizationProvider } from "@mui/x-date-pickers";
import GenericDatePicker from "../common/GenericDatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

// Static options
const enquirySources = [
  "Doctor Referral",
  "Web Search",
  "Social Media",
  "Happy Patient",
  "Local Event",
  "Advertisement",
  "Employee",
  "Family",
  "Self",
  "Other",
];

const categories = ["Price", "Service", "Support", "Quality"];

const EditEnquiry = ({ enquiryId, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { showSnackbar } = useSnackbar();
  const { auth } = useContext(authContext);
  const [currentEqnuiry, setCurrentEnquiry] = useState();

  const [form, setForm] = useState({
    id: "",
    name: "",
    countryCodeForPhone: "+91",
    PhoneNumber: "",
    age: "",
    gender: "",
    sourceCategory: "",
    sourceNotes: "",
    enquiryType: "",
    followUpDate: dayjs(),
    status: "",
    query: "",
  });

  const [formErrors, setFormErrors] = useState({
    age: "",
    PhoneNumber: "",
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  const getStyles = (option, selected, theme) => ({
    fontWeight: selected.includes(option)
      ? theme.typography.fontWeightMedium
      : theme.typography.fontWeightRegular,
  });

  //  Prefill form from enquiry prop
  useEffect(() => {
    const loadEnquiry = async () => {
      setIsSaving(true);

      if (enquiryId) {
        const result = await getEnquiriesById(auth, enquiryId);
        if (result.isSuccess) setCurrentEnquiry(result?.data);
        setForm({
          id: result?.data?.id || "",
          name: result?.data?.name || "",
          countryCodeForPhone: result?.data?.countryCodeForPhone || "+91",
          PhoneNumber: result?.data?.phoneNumber || "",
          age: result?.data?.age || "",
          gender: result?.data?.gender || "",
          sourceCategory: result?.data?.sourceCategory || "",
          sourceNotes: result?.data?.sourceNotes || "",
          enquiryType: result?.data?.enquiryType || "",
          followUpDate: result?.data?.followUpDate || "",
          status: result?.data?.status || "",
          query: result?.data?.query || "",
        });
      }
      setIsSaving(false);
    };
    loadEnquiry();
  }, [enquiryId]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "sourceCategory") {
      setForm((prev) => ({
        ...prev,
        sourceCategory: value,
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        [name]: value,
      }));
    }

    // Validation
    switch (name) {
      case "age":
        if (!/^\d*$/.test(value)) {
          setFormErrors((prev) => ({ ...prev, age: "Age must be a number" }));
        } else if (+value < 0 || +value > 120) {
          setFormErrors((prev) => ({
            ...prev,
            age: "Age must be between 0 and 120",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, age: "" }));
        }
        break;

      case "PhoneNumber":
        const phoneRegex = /^\d+$/;
        if (value && !phoneRegex.test(value)) {
          setFormErrors((prev) => ({
            ...prev,
            PhoneNumber: "Phone number must contain only digits",
          }));
        } else {
          setFormErrors((prev) => ({ ...prev, PhoneNumber: "" }));
        }
        break;
      default:
        break;
    }
  };

  const handleSave = async () => {
    setIsSubmitted(true);

    const requiredFields = [
      { field: "name", message: "Name is required." },
      { field: "PhoneNumber", message: "Contact number is required." },
      { field: "age", message: "Age is required." },
      { field: "gender", message: "Gender is required." },
      { field: "sourceCategory", message: "source category is required." },
    ];

    for (const { field, message } of requiredFields) {
      if (!form[field]) {
        showSnackbar(message, "error");
        return;
      }
    }

    if (formErrors.age || formErrors.PhoneNumber) {
      showSnackbar("Please fix validation errors before saving.", "error");
      return;
    }

    // const updatedEnquiry = {
    //   ...currentEqnuiry,
    //   age: parseInt(form.age, 10),
    //   gender: form.gender,
    //   phoneNumber: form.PhoneNumber,
    //   email: form.email,
    //   countryCodeForPhone: form.countryCodeForPhone,
    //   sourceCategory: form.mainSource,
    //   sourceNotes: form.sourceDetail,
    //   enquiryType: form.category,
    //   query: form.description,
    // };

    try {
      setIsSaving(true);
      
      const result = await updateEnquiry(auth, form);

      if (result.isSuccess) {
        showSnackbar("Enquiry details updated successfully.", "success");
        onSave(form);
      } else {
        showSnackbar("Failed to update patient details.", "error");
      }
    } catch (err) {
      showSnackbar("Failed to update enquiry", "error");
      console.error("Error saving enquiry:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <MenuBar showSave="true" onSave={handleSave} />
      <Box sx={{ position: "relative" }}>
        {isSaving && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(216, 214, 214, 0.4)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1301,
            }}
          >
            {showLoadingImage()}
          </div>
        )}

        <Grid2
          size={12}
          container
          direction="column"
          sx={{ padding: 2 }}
          spacing={1}
        >
          <Grid2
            size={12}
            container
            spacing={1}
            sx={{ maxHeight: "calc(100vh - 200px)", overflowY: "auto" }}
          >
            <Grid2
              size={{ xs: 12, sm: 12, md: 12, lg: 8, xl: 8 }}
              container
              spacing={2}
            >
              <Grid2>
                <Card
                  variant="outlined"
                  sx={{ padding: "10px", width: "100%" }}
                >
                  <CardHeader
                    subheader="Edit Enquiry Details"
                    sx={{ paddingX: 0 }}
                  />
                  <Grid2 container spacing={2}>
                    {/** First Name */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        label="Name"
                        name="name"
                        size="small"
                        margin="dense"
                        value={form.name}
                        onChange={handleChange}
                        error={isSubmitted && !form.name}
                      />
                    </Grid2>

                    {/* Age */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                      <TextField
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        label="Age"
                        name="age"
                        type="number"
                        inputProps={{ min: 0, max: 120 }}
                        value={form.age}
                        onChange={handleChange}
                        error={!!formErrors.age || (isSubmitted && !form.age)}
                        helperText={formErrors.age}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      />
                    </Grid2>

                    {/* Gender */}
                    <Grid2 size={{ xs: 12, md: 3 }}>
                      <FormControl
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        error={isSubmitted && !form.gender}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      >
                        <InputLabel id="gender-label">Gender</InputLabel>
                        <Select
                          labelId="gender-label"
                          name="gender"
                          value={form.gender}
                          onChange={handleChange}
                          input={<OutlinedInput label="Gender" />}
                          MenuProps={MenuProps}
                        >
                          {Genders.map((option) => (
                            <MenuItem
                              key={option}
                              value={option}
                              style={getStyles(option, [form.gender], theme)}
                            >
                              {option}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Country Code */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        label="Enter Country Code"
                        name="countryCodeForPhone"
                        value={form.countryCodeForPhone}
                        onChange={handleChange}
                        error={isSubmitted && !form.countryCodeForPhone}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      />
                    </Grid2>

                    {/* Phone Number */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        label="Contact Number"
                        name="PhoneNumber"
                        value={form.PhoneNumber}
                        onChange={handleChange}
                        error={
                          !!formErrors.PhoneNumber ||
                          (isSubmitted && !form.PhoneNumber)
                        }
                        helperText={formErrors.PhoneNumber}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      />
                    </Grid2>

                    

                    {/* Main Source */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <FormControl
                        fullWidth
                        required
                        margin="dense"
                        size="small"
                        error={isSubmitted && !form.sourceCategory}
                        sx={{
                          "& .MuiFormLabel-asterisk": { color: "red" },
                        }}
                      >
                        <InputLabel id="main-source-label">
                          Source Category
                        </InputLabel>
                        <Select
                          labelId="source-category-label"
                          name="sourceCategory"
                          value={form.sourceCategory}
                          onChange={handleChange}
                          input={<OutlinedInput label=" Source category" />}
                          MenuProps={MenuProps}
                        >
                          {enquirySources.map((src) => (
                            <MenuItem
                              key={src}
                              value={src}
                              style={getStyles(
                                src,
                                [form.sourceCategory],
                                theme
                              )}
                            >
                              {src}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Source Detail */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <TextField
                        fullWidth
                        margin="dense"
                        size="small"
                        label="Source Detail"
                        name="sourceNotes"
                        value={form.sourceNotes}
                        onChange={handleChange}
                      />
                    </Grid2>

                    {/* EnquiryType */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth margin="dense" size="small">
                        <InputLabel id="enquiryType-label">
                          Enquiry Type
                        </InputLabel>
                        <Select
                          labelId="enquiryType-label"
                          name="enquiryType"
                          value={form.enquiryType}
                          onChange={handleChange}
                          input={<OutlinedInput label="Enquiry Type" />}
                          MenuProps={MenuProps}
                        >
                          {categories.map((cat) => (
                            <MenuItem
                              key={cat}
                              value={cat}
                              style={getStyles(cat, [form.enquiryType], theme)}
                            >
                              {cat}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Follow Up Date */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <GenericDatePicker
                          label="Follow Up Date"
                          name="followUpDate"
                          value={
                            form.followUpDate ? dayjs(form.followUpDate) : null
                          }
                          onChange={(value) => (form.followUpDate = value)}
                          minDate={dayjs(new Date())}
                        />
                      </LocalizationProvider>
                    </Grid2>

                    {/* status */}
                    <Grid2 size={{ xs: 12, md: 6 }}>
                      <FormControl fullWidth margin="dense" size="small">
                        <InputLabel id="Status-label">Status</InputLabel>
                        <Select
                          labelId="Status-label"
                          name="status"
                          value={form.status}
                          onChange={handleChange}
                          input={<OutlinedInput label="Status" />}
                          MenuProps={MenuProps}
                        >
                          {["Open", "Close"].map((sts) => (
                            <MenuItem
                              key={sts}
                              value={sts}
                              style={getStyles(sts, [form.status], theme)}
                            >
                              {sts}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid2>

                    {/* Description */}
                    <Grid2 size={12}>
                      <TextField
                        fullWidth
                        margin="dense"
                        size="small"
                        multiline
                        rows={4}
                        label="Description"
                        name="query"
                        value={form.query}
                        onChange={handleChange}
                      />
                    </Grid2>
                  </Grid2>
                </Card>
              </Grid2>
            </Grid2>
            <Grid2
              size={{ xs: 12, sm: 12, md: 12, lg: 4, xl: 4 }}
              container
              spacing={2}
            >
              <EnquiryComments
                enquiryId={enquiryId}
                enquiryComments={currentEqnuiry?.comments}
              />
            </Grid2>
          </Grid2>
        </Grid2>
      </Box>
    </>
  );
};

export default EditEnquiry;
